import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Save, Eye, RotateCcw, Trash2, Plus } from "lucide-react";

/**
 * MisViewsBar
 * ────────────
 * Lightweight view switcher for the MIS Pack. Loads the user's saved views
 * for the given `dimension`, lets them pick one (auto-applies its
 * row_order/column_order/hidden_rows/hidden_columns to the parent state),
 * save the currently-visible layout as a new view, or delete a view.
 */
export default function MisViewsBar({
  dimension = "global", rowOrder, columnOrder, hiddenRows, hiddenColumns,
  onApplyView, onReset,
}) {
  const [views, setViews] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [openSave, setOpenSave] = useState(false);
  const [newName, setNewName] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/mis/views", { params: { dimension } });
      setViews(r.data.items || []);
      // Auto-apply the default view once
      const def = (r.data.items || []).find(v => v.is_default);
      if (def && !selectedId) {
        setSelectedId(def.id);
        onApplyView && onApplyView(def);
      }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dimension]);

  const applyId = (id) => {
    setSelectedId(id);
    if (id === "__reset") {
      onReset && onReset();
      return;
    }
    const v = views.find(v => v.id === id);
    if (v && onApplyView) onApplyView(v);
  };

  const saveNew = async () => {
    if (!newName.trim()) return toast.error("Give the view a name");
    try {
      const r = await api.post("/mis/views", {
        name: newName.trim(),
        dimension,
        row_order: rowOrder || [],
        column_order: columnOrder || [],
        hidden_rows: Array.from(hiddenRows || []),
        hidden_columns: Array.from(hiddenColumns || []),
        is_default: isDefault,
      });
      toast.success(`Saved view "${r.data.name}"`);
      setOpenSave(false); setNewName(""); setIsDefault(false);
      setSelectedId(r.data.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const overwrite = async () => {
    if (!selectedId || selectedId === "__reset") return;
    try {
      await api.patch(`/mis/views/${selectedId}`, {
        row_order: rowOrder || [],
        column_order: columnOrder || [],
        hidden_rows: Array.from(hiddenRows || []),
        hidden_columns: Array.from(hiddenColumns || []),
      });
      toast.success("View updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async () => {
    if (!selectedId || selectedId === "__reset") return;
    if (!window.confirm("Delete this saved view?")) return;
    try {
      await api.delete(`/mis/views/${selectedId}`);
      toast.success("Deleted");
      setSelectedId("");
      onReset && onReset();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`mis-views-bar-${dimension}`}>
      <span className="text-[10px] font-mono uppercase text-muted-foreground">View</span>
      <Select value={selectedId || "__reset"} onValueChange={applyId}>
        <SelectTrigger className="rounded-sm h-8 min-w-[220px] text-xs" data-testid="mis-view-select">
          <SelectValue placeholder="Default layout" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__reset">Default layout</SelectItem>
          {views.map(v => (
            <SelectItem key={v.id} value={v.id}>
              {v.name}{v.is_default ? " · default" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1"
                onClick={() => setOpenSave(true)} data-testid="mis-view-save-new">
        <Plus className="w-3 h-3" /> Save as
      </Button>
      {selectedId && selectedId !== "__reset" && (
        <>
          <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1"
                    onClick={overwrite} data-testid="mis-view-overwrite">
            <Save className="w-3 h-3" /> Update
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-red-600"
                    onClick={remove} data-testid="mis-view-delete">
            <Trash2 className="w-3 h-3" />
          </Button>
        </>
      )}
      {(rowOrder?.length || columnOrder?.length || hiddenRows?.size || hiddenColumns?.size)
        ? <Button variant="ghost" size="sm" className="rounded-sm h-8 gap-1"
                    onClick={() => { setSelectedId("__reset"); onReset && onReset(); }}
                    data-testid="mis-view-reset">
            <RotateCcw className="w-3 h-3" /> Reset
          </Button>
        : null}

      <Dialog open={openSave} onOpenChange={setOpenSave}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle>Save view</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                     className="rounded-sm mt-1"
                     placeholder="e.g. CFO monthly view" data-testid="mis-view-name" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={isDefault} onCheckedChange={(v) => setIsDefault(!!v)}
                          data-testid="mis-view-default" />
              Make this my default for {dimension} MIS
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpenSave(false)}>Cancel</Button>
              <Button onClick={saveNew}
                        className="bg-accent hover:bg-accent/90 text-white"
                        data-testid="mis-view-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
